import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const serviceList = [
  {
    id: "policy",
    title: "保單門神",
    subtitle: "健診｜整合",
    target: "個人、家庭、保單持有人",
    points: [
      "保單健診",
      "保單整合",
      "重複保障檢查",
      "醫療、意外、重大傷病、長照缺口檢視",
      "保費效益優化",
    ],
    icon: "ri-shield-check-line",
    cta: "查看保單門神",
    ctaLink: "/services/policy",
  },
  {
    id: "asset",
    title: "財產門神",
    subtitle: "資產配置｜風險控管",
    target: "想建立財務紀律、資產配置與現金流的人",
    points: [
      "資產盤點",
      "資金分艙",
      "風險承受度評估",
      "資產配置與再平衡",
      "現金流與風險管理",
    ],
    icon: "ri-bar-chart-box-line",
    cta: "查看財產門神",
    ctaLink: "/services/asset",
  },
  {
    id: "life",
    title: "人生門神",
    subtitle: "教育金｜退休｜傳承",
    target: "家庭、父母、準退休族、重視傳承安排的人",
    points: [
      "教育金規劃",
      "退休金現金流",
      "醫療與長照緩衝",
      "保險金信託",
      "贈與、繼承與家族共識",
    ],
    icon: "ri-heart-pulse-line",
    cta: "查看人生門神",
    ctaLink: "/services/life",
  },
  {
    id: "enterprise",
    title: "企業門神",
    subtitle: "守護企業主的事業",
    target: "企業主、負責人、合夥人、人資主管",
    points: [
      "薪資結構與投保差異管理",
      "勞動合約與工作規則優化",
      "員工福利與團險制度",
      "企業主風險控管",
      "接班、股權與傳承架構",
    ],
    icon: "ri-building-line",
    cta: "查看企業門神",
    ctaLink: "/services/enterprise",
  },
];

export default function Services() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="服務項目 | 保單健診・資產配置・人生規劃・企業風險 | 保險門神"
        description="保險門神四大門神系統：保單門神（健診與整合）、財產門神（資產配置與風險控管）、人生門神（教育金・退休・傳承）、企業門神（企業風險管理與傳承），全方位守護個人、家庭與企業。"
        keywords="保單健診,資產配置,退休規劃,企業保險,財務規劃,保險門神服務"
        canonicalPath="/services"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "保險門神服務項目",
          "description": "四大門神系統：保單門神、財產門神、人生門神、企業門神，從保單、財產、人生到企業建立完整守護架構。",
          "url": "__SITE_URL__/services",
          "numberOfItems": 4,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "保單門神", "url": "__SITE_URL__/services/policy" },
            { "@type": "ListItem", "position": 2, "name": "財產門神", "url": "__SITE_URL__/services/asset" },
            { "@type": "ListItem", "position": 3, "name": "人生門神", "url": "__SITE_URL__/services/life" },
            { "@type": "ListItem", "position": 4, "name": "企業門神", "url": "__SITE_URL__/services/enterprise" }
          ]
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20featuring%20four%20elegant%20golden%20portal%20doorways%20on%20deep%20black%20canvas%2C%20each%20door%20emitting%20warm%20gold%20light%2C%20representing%20four%20guardian%20systems%2C%20premium%20insurance%20service%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20metallic%20gold%20accents%2C%20cinematic%20lighting%2C%20symmetrical%20and%20powerful%20atmosphere%2C%204K%20quality&width=1920&height=600&seq=202&orientation=landscape"
              alt="服務項目 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            <div className="text-center">
              <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
                服務項目
              </h1>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[640px] mx-auto">
                四大門神系統，從保單、財產、人生到企業，建立完整守護架構。
              </p>
              <div className="w-[60px] h-[2px] bg-brand-gold mx-auto mt-4" />
            </div>
          </div>
        </section>

        {/* Service Cards */}
        <section className="pb-20 md:pb-28">
          <div className="container-main grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {serviceList.map((service, index) => (
              <ScrollReveal key={service.id} delay={index * 120}>
                <div
                  className="group bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 flex flex-col h-full"
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-brand-gold/10 shrink-0">
                      <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-brand-gold text-xl md:text-2xl">
                        <i className={service.icon} />
                      </span>
                    </div>
                    <div>
                      <span className="text-brand-gold font-bold text-[0.9375rem] md:text-[1rem] block mb-1">
                        0{index + 1}
                      </span>
                      <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] leading-[1.45]">
                        {service.title}
                      </h3>
                      <p className="text-brand-gold/80 text-[1rem] font-medium mt-0.5">
                        {service.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Target */}
                  <div className="mb-4">
                    <span className="inline-block border border-brand-border rounded-full px-3 py-1 text-brand-text-secondary text-[0.9375rem]">
                      適合對象：{service.target}
                    </span>
                  </div>

                  {/* Service Points */}
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {service.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-2.5 text-brand-text-secondary text-[1.0625rem] leading-[1.6]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={service.ctaLink}
                    className="inline-flex items-center justify-center gap-2 border border-brand-border text-white px-6 py-3 rounded-lg text-[1.0625rem] font-medium hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
                  >
                    {service.cta}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}